/*
 * @Author       : 花裤衩<白永明><yongming.bai@outlook.com>
 * @Date         : 2026-04-24 17:52:52 +0800
 * @LastEditors  : 花裤衩<白永明><yongming.bai@outlook.com>
 * @LastEditTime : 2026-04-24 18:05:49 +0800
 * @FilePath     : /expo-recommend/src/utils/timeJudge.js
 * @Description  : 时间处理
 */
/**
 * AI提示词
 * @author: BYM<yongming.bai@outlook.com>
 *
  后端返回一个奇葩的日期格式，因为数据来源不同的服务，不好整理；
  1、正常格式：YYYY-MM-DD (正常的年月日格式)
  2、不正常格式1：YYYY.MM.DD-DD （表示从某一天到某一天 可以理解为 开始：YYYY-MM-DD，结束：YYYY-MM-DD）
  3、不正常格式2：YYYY-MM-DD/YYYY-MM-DD （表示从某一天到某一天 可以理解为 开始：YYYY-MM-DD，结束：YYYY-MM-DD）
  4、不正常格式3：YYYY.MM.DD (单纯年月日 YYYY-MM-DD)
  5、可能还有其他日期格式，暂不考虑
  我的需求是：整理这些时间 统一整理为YYYY-MM-DD格式，方法内要实现返回界面可直接展示的日期文本
  1、timeJudge方法中 我会传入两个参数，参数1必传：（正常或者不正常日期）；参数2非必传：(startTime: HH:mm:ss, endTime: HH:mm:ss)
  2、过滤整合参数，如果参数1是单一日期格式，中文返回格式：“2025年11月21日 周五 09:00–10:00”；英文返回格式：“Fri, Nov 21, 2025 • 9:00–10:00 AM”；如果没有参数2  时间部分可过滤掉不显示
  3、过滤整合参数，如果参数1是两个日期格式，中文返回格式：“2025年11月21日-23日”； 英文返回格式：“Nov 21 – 23, 2025”；不显示参数2的内容 直接过滤掉
  4、容错处理，如果参数1 处理完也不符合时间格式 就返回原内容 要过滤掉参数2  不回显参数2
 */
import i18n from '@/lang'

/**
 * 安全解析日期字符串为本地时区 Date 对象
 * 支持 YYYY-MM-DD 和 YYYY.MM.DD 格式
 * @param {string} str
 * @returns {Date | null}
 */
function _parseDateStr(str) {
  if (!str || typeof str !== 'string') return null
  const normalized = str.trim().replace(/\./g, '-')
  const match = normalized.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/)
  if (!match) return null
  const date = new Date(parseInt(match[1]), parseInt(match[2]) - 1, parseInt(match[3]))
  return isNaN(date.getTime()) ? null : date
}

/**
 * 解析时间字符串（HH:mm:ss / HH:mm）
 * @param {string} str
 * @returns {{ display24: string, display12: string, period: 'AM' | 'PM' } | null}
 */
function _parseTimeStr(str) {
  if (!str || typeof str !== 'string') return null
  const match = str.match(/^(\d{1,2}):(\d{2})/)
  if (!match) return null
  const h = parseInt(match[1])
  const m = parseInt(match[2])
  const period = h < 12 ? 'AM' : 'PM'
  const h12 = h % 12 || 12
  return {
    display24: `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`,
    display12: `${h12}:${String(m).padStart(2, '0')}`,
    period
  }
}

/**
 * 格式化中文时间范围（24小时制）
 * @param {string} startTime
 * @param {string} endTime
 * @returns {string}
 */
function _formatTimeRangeCn(startTime, endTime) {
  const s = _parseTimeStr(startTime)
  const e = _parseTimeStr(endTime)
  if (s && e) return `${s.display24}–${e.display24}`
  if (s) return s.display24
  if (e) return e.display24
  return ''
}

/**
 * 格式化英文时间范围（12小时制 + AM/PM）
 * @param {string} startTime
 * @param {string} endTime
 * @returns {string}
 */
function _formatTimeRangeEn(startTime, endTime) {
  const s = _parseTimeStr(startTime)
  const e = _parseTimeStr(endTime)
  if (s && e) {
    if (s.period === e.period) {
      return `${s.display12}–${e.display12}${s.period}`
    }
    return `${s.display12}${s.period}–${e.display12}${e.period}`
  }
  if (s) return `${s.display12}${s.period}`
  if (e) return `${e.display12}${e.period}`
  return ''
}

/**
 * 日期时间智能格式化
 * 支持后端多种日期格式的统一处理
 *
 * @author: BYM<yongming.bai@outlook.com>
 * @param {string} dateStr - 日期字符串，支持以下格式：
 *   - YYYY-MM-DD（正常格式）
 *   - YYYY.MM.DD-DD（日期范围，结束日仅天）
 *   - YYYY-MM-DD/YYYY-MM-DD（日期范围，完整起止日期）
 *   - YYYY.MM.DD（点分隔日期）
 * @param {Object} [options] - 配置选项
 * @param {string} [options.startTime] - 开始时间 HH:mm:ss
 * @param {string} [options.endTime] - 结束时间 HH:mm:ss
 * @returns {string} 格式化后的日期文本
 *
 * @example
 * // 单一日期 + 时间
 * timeJudge('2025-11-21', { startTime: '09:00:00', endTime: '10:00:00' })
 * // cn: '2025年11月21日 周五 09:00–10:00'
 * // en: 'Fri, Nov 21, 2025 • 9:00–10:00AM'
 *
 * // 单一日期，无时间
 * timeJudge('2025-11-21')
 * // cn: '2025年11月21日 周五'
 * // en: 'Fri, Nov 21, 2025'
 *
 * // 日期范围（同月）
 * timeJudge('2025.11.21-23')
 * // cn: '2025年11月21日-23日'
 * // en: 'Nov 21–23, 2025'
 *
 * // 日期范围（完整起止日期）
 * timeJudge('2025-11-21/2025-12-01')
 * // cn: '2025年11月21日-12月1日'
 * // en: 'Nov 21–Dec 1, 2025'
 *
 * // 容错：无法解析时返回原内容
 * timeJudge('invalid-date', { startTime: '09:00:00' })
 * // → 'invalid-date'（过滤参数2）
 */
export function timeJudge(dateStr, options = {}) {
  if (!dateStr || typeof dateStr !== 'string') return dateStr ?? ''

  const { startTime, endTime } = options
  const locale = i18n.global.locale.value || 'cn'
  const isEn = locale === 'en'

  const weekMap = {
    en: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
    cn: ['周日', '周一', '周二', '周三', '周四', '周五', '周六']
  }
  const monthNamesEn = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

  // ---- 解析日期，判断是否为日期范围 ----
  let isRange = false
  let startDate = null
  let endDate = null

  // 格式2: YYYY-MM-DD/YYYY-MM-DD（日期范围，完整起止日期）
  if (dateStr.includes('/')) {
    const parts = dateStr.split('/')
    if (parts.length === 2) {
      const s = _parseDateStr(parts[0])
      const e = _parseDateStr(parts[1])
      if (s && e) {
        isRange = true
        startDate = s
        endDate = e
      }
    }
  }

  // 格式1: YYYY.MM.DD-DD（日期范围，结束日仅天）
  if (!isRange) {
    const rangeMatch = dateStr.match(/^(\d{4})\.(\d{1,2})\.(\d{1,2})-(\d{1,2})$/)
    if (rangeMatch) {
      const s = new Date(parseInt(rangeMatch[1]), parseInt(rangeMatch[2]) - 1, parseInt(rangeMatch[3]))
      const e = new Date(parseInt(rangeMatch[1]), parseInt(rangeMatch[2]) - 1, parseInt(rangeMatch[4]))
      if (!isNaN(s.getTime()) && !isNaN(e.getTime())) {
        isRange = true
        startDate = s
        endDate = e
      }
    }
  }

  // 格式3/0: YYYY.MM.DD 或 YYYY-MM-DD（单一日期）
  if (!isRange) {
    startDate = _parseDateStr(dateStr)
  }

  // 容错：无法解析时返回原内容，过滤参数2
  if (!startDate) return dateStr

  // ---- 日期范围格式（忽略参数2的时间） ----
  if (isRange && endDate) {
    if (isEn) {
      const sy = startDate.getFullYear()
      const sm = monthNamesEn[startDate.getMonth()]
      const sd = startDate.getDate()
      const ey = endDate.getFullYear()
      const em = monthNamesEn[endDate.getMonth()]
      const ed = endDate.getDate()

      if (sy === ey && startDate.getMonth() === endDate.getMonth()) {
        // 同年同月: Nov 21–23, 2025
        return `${sm} ${sd}–${ed}, ${sy}`
      } else if (sy === ey) {
        // 同年不同月: Nov 21–Dec 1, 2025
        return `${sm} ${sd}–${em} ${ed}, ${sy}`
      }
      // 不同年: Nov 21, 2025–Jan 15, 2026
      return `${sm} ${sd}, ${sy}–${em} ${ed}, ${ey}`
    }

    // 中文日期范围
    const sy = startDate.getFullYear()
    const sm = startDate.getMonth() + 1
    const sd = startDate.getDate()
    const ey = endDate.getFullYear()
    const em = endDate.getMonth() + 1
    const ed = endDate.getDate()

    if (sy === ey && sm === em) {
      // 同年同月: 2025年11月21日-23日
      return `${sy}年${sm}月${sd}日-${ed}日`
    } else if (sy === ey) {
      // 同年不同月: 2025年11月21日-12月1日
      return `${sy}年${sm}月${sd}日-${em}月${ed}日`
    }
    // 不同年: 2025年11月21日-2026年1月15日
    return `${sy}年${sm}月${sd}日-${ey}年${em}月${ed}日`
  }

  // ---- 单一日期格式 ----
  const y = startDate.getFullYear()
  const m = startDate.getMonth() + 1
  const d = startDate.getDate()
  const week = weekMap[isEn ? 'en' : 'cn'][startDate.getDay()]

  if (isEn) {
    const datePart = `${week}, ${monthNamesEn[startDate.getMonth()]} ${d}, ${y}`
    const timePart = _formatTimeRangeEn(startTime, endTime)
    return timePart ? `${datePart} ${timePart}` : datePart
  }

  // 中文单一日期
  const datePart = `${y}年${m}月${d}日 ${week}`
  const timePart = _formatTimeRangeCn(startTime, endTime)
  return timePart ? `${datePart} ${timePart}` : datePart
}
